import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import PageLayout from "./components/PageLayout";
import { getMerchAsset } from "./data/merchAssets";
import { getMerchProducts } from "./services/merchProducts";
import MerchProductVisual from "./components/merch/MerchProductVisual";
import { MerchResponsiveStyles, cardStyle, disabledButtonStyle, galleryButtonStyle, optionButtonStyle, primaryButtonStyle, quantityButtonStyle, sectionLabelStyle, selectedGalleryButtonStyle, selectedOptionButtonStyle, specificationLabelStyle, specificationValueStyle, statusBadgeStyle } from "./components/merch/MerchStyles";

function MerchProduct() {
  const { productId } = useParams();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    getMerchProducts({ publishedOnly: true })
      .then((items) => setProduct(items.find((item) => item.id === productId) || null))
      .catch((loadError) => setError(loadError.message || "Product could not be loaded."))
      .finally(() => setLoading(false));
  }, [productId]);

  if (loading) return <PageLayout maxWidth="700px" showBack eyebrow="Merch Engine" title="Loading product…" />;
  if (error) return <PageLayout maxWidth="700px" showBack eyebrow="Merch Engine" title="Product unavailable."><p role="alert" style={{ color: "#ff8b87" }}>{error}</p></PageLayout>;
  if (!product) {
    return <PageLayout maxWidth="700px" showBack eyebrow="Merch Engine" title="Product not found."><div style={{ ...cardStyle, padding: "32px" }}><p style={{ margin: "0 0 22px", color: "#aaa" }}>This product is not part of the current collection.</p><Link className="merch-action" to="/merch/collection/identity" style={primaryButtonStyle}>Back to Collection</Link></div></PageLayout>;
  }
  return <ProductDetails product={product} />;
}

function ProductDetails({ product }) {
  const [selectedColor, setSelectedColor] = useState(product.availableColors[0]);
  const [selectedSize, setSelectedSize] = useState(product.availableSizes[0]);
  const [quantity, setQuantity] = useState(1);
  const [galleryView, setGalleryView] = useState("back");
  const selectedMockup = product.mockupVariants?.find((mockup) => (
    mockup.color === selectedColor && mockup.view === galleryView
  ));
  const galleryImage = selectedMockup?.url
    || product.mockupUrl
    || getMerchAsset(product.galleryKey, selectedColor, galleryView);

  return (
    <PageLayout pageClassName="merch-page merch-product-page" maxWidth="1100px" showBack eyebrow={product.collectionNumber} title={product.name} subtitle={product.descriptor}>
      <MerchResponsiveStyles />
      <div className="merch-detail-grid" style={{ marginTop: "clamp(38px, 6vw, 68px)" }}>
        <section className="product-preview-section" aria-label={`${product.name} gallery`}>
          <div className="merch-preview-shell">
          <div className="product-preview" style={cardStyle}>
            <MerchProductVisual product={product} color={selectedColor} view={galleryView} imageSrc={galleryImage} />
          </div>
            <div className="merch-gallery-toggle" style={galleryToggleStyle} role="group" aria-label="Product preview view">
              {[["front", "Front"], ["back", "Back"]].map(([value, label]) => (
                <button className="merch-option" key={value} type="button" aria-pressed={galleryView === value} onClick={() => setGalleryView(value)} style={galleryView === value ? selectedGalleryButtonStyle : galleryButtonStyle}>{label}</button>
              ))}
            </div>
          </div>
          {!product.mockupUrl && <p style={{ margin: "12px 0 0", color: "#747474", fontSize: "12px", lineHeight: 1.5 }}>Approved garment mockup coming soon.</p>}
        </section>

        <section className="merch-product-info" aria-label="Product information">
          <p className="merch-product-collection" style={sectionLabelStyle}>{product.collectionNumber} — {product.collection}</p>
          <h2 className="merch-product-title" style={{ margin: "0 0 9px", fontSize: "clamp(34px, 5vw, 56px)", lineHeight: 1, letterSpacing: "-0.045em" }}>{product.name}</h2>
          <p className="merch-product-subtitle" style={{ margin: "0 0 16px", color: "#ccc", fontSize: "18px" }}>{product.descriptor}</p>
          {product.price != null && <p className="merch-product-price" style={{ margin: "0 0 20px", fontSize: "22px", fontWeight: 900 }}>${Number(product.price).toFixed(2)}</p>}
          <div className="merch-feature-cards" style={{ display: "grid", gridTemplateColumns: "repeat(3, minmax(0, 1fr))", gap: "1px", background: "#303030", border: "1px solid #303030", borderRadius: "12px", overflow: "hidden", marginBottom: "32px" }}>
            <QuickSpec value={product.weight} /><QuickSpec value={product.material} /><QuickSpec value="Oversized fit" />
          </div>
          <p className="merch-product-description" style={{ margin: "0 0 18px", color: "#aaa", lineHeight: 1.7 }}>{product.description}</p>
          <span className="merch-sample-badge" style={{ ...statusBadgeStyle, marginBottom: "30px" }}>{product.orderStatus}</span>

          <div className="product-options">
          <OptionGroup className="color-options-group" label="Color" values={product.availableColors} selected={selectedColor} onSelect={setSelectedColor} />
          <OptionGroup className="size-options-group" label="Size" values={product.availableSizes} selected={selectedSize} onSelect={setSelectedSize} />

          <fieldset className="quantity-options-group" style={{ border: 0, padding: 0, margin: "0 0 26px" }}>
            <legend style={{ marginBottom: "12px", fontWeight: 900 }}>Quantity</legend>
            <div style={{ display: "inline-flex", alignItems: "center", border: "1px solid #3a3a3a", borderRadius: "9px", overflow: "hidden", background: "#181818" }}>
              <button className="merch-option" type="button" aria-label="Decrease quantity" onClick={() => setQuantity((value) => Math.max(1, value - 1))} disabled={quantity === 1} style={{ ...quantityButtonStyle, opacity: quantity === 1 ? .35 : 1 }}>−</button>
              <output aria-live="polite" aria-label={`Quantity ${quantity}`} style={{ minWidth: "60px", textAlign: "center", fontWeight: 900 }}>{quantity}</output>
              <button className="merch-option" type="button" aria-label="Increase quantity" onClick={() => setQuantity((value) => Math.min(10, value + 1))} disabled={quantity === 10} style={{ ...quantityButtonStyle, opacity: quantity === 10 ? .35 : 1 }}>+</button>
            </div>
          </fieldset>
          </div>

          <button className="merch-purchase-button" type="button" disabled={true} style={disabledButtonStyle}>Coming Soon</button>
          <p className="merch-sample-notice" style={{ margin: "13px 0 34px", color: "#999", textAlign: "center", fontSize: "13px" }}>{product.orderStatus}</p>

          <div className="merch-garment-details" style={{ borderTop: "1px solid #2a2a2a", paddingTop: "28px" }}>
            <h3 style={{ margin: "0 0 20px", fontSize: "22px" }}>Garment details</h3>
            <div style={{ display: "grid", gap: "20px" }}><Spec label="Garment" value={product.garment} /><Spec label="Fit" value={product.fit} /><Spec label="Front design" value={product.frontDesign} /><Spec label="Back design" value={product.backDesign} /></div>
            <ul style={{ margin: "24px 0 0", paddingLeft: "20px", color: "#aaa", lineHeight: 1.8 }}>{product.details.map((detail) => <li key={detail}>{detail}</li>)}</ul>
          </div>
        </section>
      </div>
    </PageLayout>
  );
}

function OptionGroup({ className, label, values, selected, onSelect }) {
  const optionsClassName = className === "color-options-group" ? "color-options" : "size-options";
  return <fieldset className={className} style={{ border: 0, padding: 0, margin: "0 0 26px" }}><legend style={{ marginBottom: "12px", fontWeight: 900 }}>{label}</legend><div className={optionsClassName} style={{ display: "flex", flexWrap: "wrap", gap: "8px" }}>{values.map((value) => <button className="merch-option" key={value} type="button" aria-pressed={selected === value} onClick={() => onSelect(value)} style={selected === value ? selectedOptionButtonStyle : optionButtonStyle}>{value}</button>)}</div></fieldset>;
}

function QuickSpec({ value }) {
  return <div style={{ minHeight: "76px", padding: "14px 10px", background: "#181818", display: "flex", alignItems: "center", justifyContent: "center", textAlign: "center", fontSize: "12px", fontWeight: 800, lineHeight: 1.35 }}>{value}</div>;
}

function Spec({ label, value }) {
  return <div><p style={specificationLabelStyle}>{label}</p><p style={specificationValueStyle}>{value}</p></div>;
}

const galleryToggleStyle = {
  position: "absolute",
  left: "50%",
  bottom: "18px",
  display: "flex",
  gap: "6px",
  padding: "6px",
  border: "1px solid rgba(255,255,255,.16)",
  borderRadius: "12px",
  background: "rgba(10,10,10,.88)",
  transform: "translateX(-50%)",
  boxShadow: "0 10px 30px rgba(0,0,0,.35)",
  zIndex: 4
};

export default MerchProduct;
